import React, { useMemo, useState } from "react";
import { Button } from "@/components/fun-ui/Button";
import { cn } from "@/lib/utils";
import { TokenIcon } from "@/components/fun-ui/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  // DialogTrigger,
} from "@/components/fun-ui/Dialog";
import { ColConsumePrice } from "@/stores/slices/paperSlice";
import { usePapers } from "@/stores/appStore";

type ConfirmPriceDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  colConsumePrice: ColConsumePrice;
  subjectId?: string;
  cancel?: () => void;
  confirm?: (insufficient: boolean) => Promise<void>;
  title?: string;
  cancelLabel?: string;
};

export const ConfirmPriceDialog: React.FC<ConfirmPriceDialogProps> = ({
  open,
  setOpen,
  colConsumePrice = null,
  subjectId = '',
  cancel,
  confirm,
  title = '',
  cancelLabel,
}) => {
  const [generateButtonDisabled, setGenerateButtonDisabled] = useState(false);
  const TITLE = '本次生成需要消耗';
  const CALCULATING = '计算中...';

  // 获取 base paper list 来计算剔除的摘要型文献数量
  const basePapers = usePapers('', 'base');
  const excludedAbstractCount = useMemo(() => {
    return basePapers.filter((paper) => {
      const isAbstract = paper?.attachmentId?.toString() === '0';
      if (!isAbstract) return false;
      if (!subjectId) return true;
      return (paper.subjects || []).some((s) => s.id === subjectId);
    }).length;
  }, [basePapers, subjectId]);

  const {
    stdCount = 0,
    stdConsume = 0,
    stdUnitPrice = 0,
    customUnitPrice = 0,
    customCount = 0,
    customConsume = 0,
    chartUnitPrice = 0,
    chartCount = 0,
    chartConsume = 0,
    userBalance = 0,
  } = colConsumePrice || {};
  const total = Number((stdConsume + customConsume + chartConsume).toFixed(0));

  const insufficient = useMemo(() => {
    return (typeof total === 'number' && userBalance < total);
  }, [total, userBalance]);

  const list: {
    label: string;
    consume: number | string;
  }[] = [];

  if (stdConsume) {
    list.push({
      label: `预设列提取格数：${colConsumePrice ? stdCount : CALCULATING}（每格 ${stdUnitPrice} 飞豆 ）`,
      consume: colConsumePrice ? stdConsume : CALCULATING,
    });
  }
  if (customConsume) {
    list.push({
      label: `自定义列提取格数：${colConsumePrice ? customCount : CALCULATING}（每格 ${customUnitPrice} 飞豆 ）`,
      consume: colConsumePrice ? customConsume : CALCULATING,
    });
  }
  if (chartConsume) {
    list.push({
      label: `智能解析列提取格数：${colConsumePrice ? chartCount : CALCULATING}（每格 ${chartUnitPrice} 飞豆 ）`,
      consume: colConsumePrice ? chartConsume : CALCULATING,
    });
  }

  const description = useMemo(() => {
    return `${title || TITLE}：${total ? `${total} 飞豆` : CALCULATING}`;
  }, [total, title]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="px-[60px] pt-16 pb-10 w-full sm:max-w-[620px] gap-9">
        <DialogDescription className="sr-only">
          {description}
        </DialogDescription>
        <div className="flex flex-col gap-8 justify-center items-center">
          <h3 className="text-[30px] font-medium">
            {description}
          </h3>
          <div className="text-justify flex flex-col gap-2">
            {
              list.map(({ label, consume }) => {
                return (
                  <div key={label} className="flex items-center justify-between gap-4 text-sm">
                    <span>
                      {label}
                    </span>
                    <div
                      className={cn(
                        "flex items-center justify-center w-fit",
                        "space-x-[1px]"
                      )}
                    >
                      <TokenIcon size={24} />
                      <span> × {consume}</span>
                    </div>
                  </div>
                )
              })
            }
            {
              excludedAbstractCount > 0 ? (
                <div className="text-sm text-annotation-foreground text-center">
                  已剔除 {excludedAbstractCount} 篇摘要型文献
                </div>
              ) : null
            }
            {
              insufficient ? (
                <div className="text-destructive text-sm text-center">飞豆余额不足，请先充值</div>
              ) : null
            }
          </div>
        </div>
        <DialogFooter className="flex gap-7 w-full">
          <Button
            className="flex-1 h-12"
            size="lg"
            variant="outline"
            onClick={() => {
              cancel?.();
            }}
          >
            {cancelLabel || '取消'}
          </Button>
          <Button
            className="flex-1 h-12"
            size="lg"
            disabled={generateButtonDisabled}
            onClick={async () => {
              if (generateButtonDisabled) return;
              setGenerateButtonDisabled(true);
              await confirm?.(insufficient);
              setGenerateButtonDisabled(false);
            }}
          >
            {insufficient ? '去充值' : '确认支付'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};